/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable react/display-name */
import { Tooltip, Typography } from '@material-ui/core';
import {
  AddBox,
  ArrowUpward,
  Check,
  ChevronLeft,
  ChevronRight,
  Clear,
  DeleteOutline,
  FilterList,
  FirstPage,
  LastPage,
  Remove,
  Search,
  ViewColumn,
} from '@material-ui/icons';
import { AxiosResponse } from 'axios';
import MaterialTable, { Column, Icons, Options, Query, QueryResult } from 'material-table';
import moment from 'moment-timezone';
import React, { forwardRef, useRef } from 'react';
import { IReportInfo, IReportsResponse } from '../../services/types';
import StatusCell from './StatusCell';
import TargetCell from './TargetCell';
import UserCell from './UserCell';
import { updateMultipleReportStatus, getReports } from '../../services/api/Report';

interface IProps {
  storyId?: number;
}

export interface IRowData extends IReportInfo {
  target: string;
}

const tableIcons: Icons = {
  Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
  Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
  Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
  Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
  FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
  LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
  NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  PreviousPage: forwardRef((props, ref) => <ChevronLeft {...props} ref={ref} />),
  ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
  SortArrow: forwardRef((props, ref) => <ArrowUpward {...props} ref={ref} />),
  ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
  ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
};

const ReportList = (): JSX.Element => {
  const tableRef = useRef<MaterialTable<IRowData>>(null);
  const tableOptions = useRef<Options<any>>({
    search: false,
    selection: true,
    pageSize: 10,
  });
  const columns = useRef<Column<IRowData>[]>([
    {
      title: 'Target',
      field: 'target',
      render: (rowData: IRowData) => <TargetCell rowData={rowData} />,
    },
    {
      title: 'Status',
      field: 'status',
      render: (rowData: IRowData) => <StatusCell rowData={rowData} onChangeStatus={handleChangeStatus} />,
    },
    {
      title: 'User',
      field: 'userId',
      type: 'numeric',
      render: (rowData: IRowData) => <UserCell rowData={rowData} />,
    },
    {
      title: 'Category',
      field: 'category1',
      render: (rowData: IRowData) => {
        return (
          <Tooltip title={rowData.text} placement={'bottom-start'} interactive>
            <div>
              <Typography variant="body2">{`- ${rowData.category1}`}</Typography>
              {!!rowData.category2 && <Typography variant="body2">{`- ${rowData.category2}`}</Typography>}
              {!!rowData.category3 && <Typography variant="body2">{`- ${rowData.category3}`}</Typography>}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Reported At',
      field: 'createdAt',
      render: (rowData: IRowData): JSX.Element => {
        return <Typography variant={'body2'}>{`${moment(rowData.createdAt).format('ddd, MMM D, YYYY HH:mm')}`}</Typography>;
      },
    },
  ]);

  const handleChangeStatus = (ids: number[], status: 'open' | 'resolved' | 'dismissed'): void => {
    updateMultipleReportStatus(ids, status).then(() => {
      if (!tableRef.current) {
        return;
      }

      (tableRef.current as any).onQueryChange();
    });
  };

  const loadData = async (query: Query<IRowData>): Promise<QueryResult<IRowData>> => {
    return new Promise(resolve => {
      getReports({ page: query.page + 1, limit: query.pageSize - 1 })
        .then((response: AxiosResponse<IReportsResponse>) => {
          const reports = response.data[0] as IRowData[];
          const totalCount = response.data[1] as number;

          resolve({
            data: reports,
            page: query.page,
            totalCount: Math.ceil(totalCount / query.pageSize) || 1,
          });
        })
        .catch(() => {
          resolve({
            data: [],
            page: 0,
            totalCount: 0,
          });
        });
    });
  };

  return (
    <MaterialTable tableRef={tableRef} options={tableOptions.current} icons={tableIcons} columns={columns.current} data={loadData} title="Reports" />
  );
};

export default ReportList;
